import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import { TOOL_ID, FOLDER_NAME } from '@/consts/configs';
import type { ContentFileAttributes } from '@/types';

import { createNavigationSlice, type NavigationSlice } from './navigation';
import {
  createLibrarySlice,
  libraryInitialState,
  type LibrarySlice,
} from './library';
import {
  createPresetSlice,
  presetInitialState,
  type PresetSlice,
} from './preset';
import {
  createInputsSlice,
  inputsInitialState,
  type InputsSlice,
} from './inputs';
import {
  createGenerationSlice,
  generationInitialState,
  type GenerationSlice,
} from './generation';
import {
  createResultSlice,
  resultInitialState,
  type ResultSlice,
} from './result';
import { createFilesSlice, type FilesSlice } from './files';
import { createUserSlice, type UserSlice } from './user';
import { createProjectSlice, type ProjectSlice } from './project';
import { createCanvasSlice, type CanvasSlice } from './canvas';
import { createToastSlice, type ToastSlice } from './toast';
import { createUploadSlice, type UploadSlice } from './upload';

// a === b for primitive data types, shallow for complex data types
const compare = <T>(a: T, b: T) => a === b || shallow(a, b);

interface DriveSlice {
  toolId: string;
  folderName: string;
  attributes?: ContentFileAttributes;
}

interface ResetSlice {
  resetCreate: () => void;
  resetAll: () => void;
}

export type AppState = NavigationSlice &
  LibrarySlice &
  PresetSlice &
  InputsSlice &
  GenerationSlice &
  ResultSlice &
  FilesSlice &
  UserSlice &
  ProjectSlice &
  CanvasSlice &
  ToastSlice &
  UploadSlice &
  DriveSlice &
  ResetSlice;

export const useAppStore = createWithEqualityFn<AppState>()(
  (...args) => ({
    ...createNavigationSlice(...args),
    ...createLibrarySlice(...args),
    ...createPresetSlice(...args),
    ...createInputsSlice(...args),
    ...createGenerationSlice(...args),
    ...createResultSlice(...args),
    ...createFilesSlice(...args),
    ...createUserSlice(...args),
    ...createProjectSlice(...args),
    ...createCanvasSlice(...args),
    ...createToastSlice(...args),
    ...createUploadSlice(...args),

    toolId: TOOL_ID,
    folderName: FOLDER_NAME,

    resetCreate: () => {
      const [set] = args;
      set({
        ...presetInitialState,
        ...inputsInitialState,
        ...resultInitialState,
        ...generationInitialState,
        hasAutoSelectedPreset: libraryInitialState.hasAutoSelectedPreset,
      });
    },

    resetAll: () => {
      const [set, , store] = args;
      set(store.getInitialState(), true);
    },
  }),
  compare
);

export const getAppStore = useAppStore.getState;
export const setAppStore = useAppStore.setState;

export type { AppState as IAppStore };
